import React, { useState, useEffect, useRef, useCallback } from "react";
import { EnrichedField, FieldType } from "../../api/enrichApi";
import {
  StringField,
  IntegerField,
  DecimalField,
  BooleanField,
  SingleSelectField,
  MultiSelectField,
} from "./EnrichFieldInputs";

type Props = {
  field: EnrichedField;
  onSave: (enrich_result_id: number, fieldValue: any) => Promise<void>;
  disabled?: boolean;
};

export const EnrichFieldRenderer: React.FC<Props> = ({
  field,
  onSave,
  disabled = false,
}) => {
  const [localValue, setLocalValue] = useState<any>(field.value);
  const [isSaving, setIsSaving] = useState(false);
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Update local value when field value changes (e.g., after revert)
  useEffect(() => {
    setLocalValue(field.value);
  }, [field.value]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  const saveValue = useCallback(
    async (newValue: any): Promise<void> => {
      setIsSaving(true);
      try {
        await onSave(field.id, newValue);
      } catch (error) {
        console.error("Failed to save field:", error);
        // Revert to original value on error
        setLocalValue(field.value);
      } finally {
        setIsSaving(false);
      }
    },
    [field.key, field.value, onSave]
  );

  const handleChange = (
    newValue: any,
    shouldDebounce: boolean = false
  ): void => {
    setLocalValue(newValue);

    // Clear existing timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    if (shouldDebounce) {
      // Debounce for text/numeric inputs (500ms)
      saveTimeoutRef.current = setTimeout(() => {
        saveValue(newValue);
      }, 500);
    } else {
      // Immediate save for boolean, select, etc.
      saveValue(newValue);
    }
  };

  const renderField = (): JSX.Element => {
    const isFieldDisabled = disabled || isSaving;

    switch (field.type) {
      case FieldType.STRING:
        return (
          <StringField
            value={localValue}
            onChange={(value) => handleChange(value, true)}
            disabled={isFieldDisabled}
          />
        );

      case FieldType.INTEGER:
        return (
          <IntegerField
            value={localValue}
            onChange={(value) => handleChange(value, true)}
            disabled={isFieldDisabled}
          />
        );

      case FieldType.DECIMAL:
        return (
          <DecimalField
            value={localValue}
            onChange={(value) => handleChange(value, true)}
            disabled={isFieldDisabled}
          />
        );

      case FieldType.BOOLEAN:
        return (
          <BooleanField
            value={localValue}
            onChange={(value) => handleChange(value, false)}
            disabled={isFieldDisabled}
          />
        );

      case FieldType.SINGLE_SELECT_LIST:
        return (
          <SingleSelectField
            value={localValue}
            onChange={(value) => handleChange(value, false)}
            options={field.options}
            disabled={isFieldDisabled}
          />
        );

      case FieldType.MULTI_SELECT_LIST:
        return (
          <MultiSelectField
            value={localValue}
            onChange={(value) => handleChange(value, false)}
            options={field.options}
            disabled={isFieldDisabled}
          />
        );

      default:
        // Fallback for unknown types or LANGUAGE_STRING (not supported)
        return (
          <div className="tw-text-sm tw-text-gray-500">
            {typeof localValue === "object"
              ? JSON.stringify(localValue)
              : String(localValue)}
          </div>
        );
    }
  };

  return (
    <div className="tw-relative">
      {renderField()}

      {/* Saving indicator - fixed position overlay */}
      {isSaving && (
        <div className="tw-absolute tw-top-1 tw-right-1 tw-bg-white tw-px-2 tw-py-1 tw-rounded tw-shadow-sm tw-border tw-border-gray-200">
          <span className="tw-text-xs tw-text-gray-500 tw-animate-pulse">
            Saving...
          </span>
        </div>
      )}
    </div>
  );
};
